import { useAppleAuthConfig } from '../../features/settings';
import { formatDateTime } from '../../lib/format';

const inputCls =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary';
const textareaCls =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono leading-6 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary';

export function AppleAuthPage() {
  const { data, form, loading, saving, error, updateField, save, reload } = useAppleAuthConfig();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 rounded-full border-2 border-gray-300 border-t-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-20 text-center">
        <p className="mb-3 text-sm text-red-500">{error}</p>
        <button onClick={reload} className="text-sm text-primary hover:underline">
          重试
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-6">
      <div className="space-y-2">
        <p className="text-sm text-gray-500">
          配置移动端 Apple ID 验证登录使用的 auth endpoint，以及邮箱 Apple ID 可绑定的邮箱后缀。保存后无需重新打包，App 下一次发起 Apple ID 验证前会读取新规则。
        </p>
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-xs leading-5 text-amber-700">
          只填写完整 HTTPS URL。当前已知可用默认值为 https://auth.itunes.apple.com/auth/v1/native/fast/
        </div>
      </div>

      <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-5">
        <div>
          <label className="mb-1 block text-sm text-gray-700">Apple 登录接口地址</label>
          <input
            value={form.auth_endpoint}
            onChange={(e) => updateField('auth_endpoint', e.target.value)}
            className={inputCls}
            spellCheck={false}
            placeholder="https://auth.itunes.apple.com/auth/v1/native/fast/"
          />
        </div>

        <div className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-3">
          <div>
            <div className="text-sm font-medium text-gray-900">限制邮箱 Apple ID 后缀</div>
            <div className="mt-1 text-xs text-gray-500">开启后，仅邮箱格式 Apple ID 会检查下方后缀；手机号或纯数字 Apple ID 不受影响。</div>
          </div>
          <label className="relative inline-flex cursor-pointer items-center">
            <input
              type="checkbox"
              checked={form.email_domain_restriction_enabled}
              onChange={(e) => updateField('email_domain_restriction_enabled', e.target.checked)}
              className="peer sr-only"
            />
            <span className="h-6 w-11 rounded-full bg-gray-200 transition-colors peer-checked:bg-primary" />
            <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5" />
          </label>
        </div>

        <div>
          <label className="mb-1 block text-sm text-gray-700">允许的邮箱后缀</label>
          <textarea
            value={form.allowed_email_domains.join('\n')}
            onChange={(e) => updateField('allowed_email_domains', e.target.value.split(/\r?\n|,/))}
            className={textareaCls}
            rows={7}
            spellCheck={false}
            placeholder={"@icloud.com\n@qq.com\n@163.com"}
          />
          <p className="mt-2 text-xs text-gray-500">每行一个后缀，必须以 @ 开头；支持英文逗号分隔，保存时会自动去重并转为小写。</p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-primary-hover disabled:opacity-50 transition-colors"
          >
            {saving && (
              <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            )}
            保存配置
          </button>
        </div>
      </section>

      {data?.updated_at && (
        <div className="text-xs text-gray-400 space-y-1">
          <p>最后更新：{formatDateTime(data.updated_at)}</p>
          {data.updated_by != null && <p>操作人 ID：{data.updated_by}</p>}
        </div>
      )}
    </div>
  );
}
